import { AutoFormFields } from 'lib/LegacyForm';
import { ContentModule, isLoyaltyBalanceModule } from '../models/ContentModule';
import { getPerkOpacity } from 'components/perks/helpers';

export const defaultLoyaltyBalanceVisualisation = {
    textColour: '#000000',
    backgroundOverlayColour: '#ffffff',
    textAlignment: 'CENTER',
    verticalAlignment: 'MIDDLE',
    subtitleText: '{{balance}}'
};

export const createModuleLoyaltyBalanceForm: AutoFormFields = {
    title: {
        label: 'Title',
        type: 'text',
        width: 9
    },
    index: {
        label: 'Sort',
        type: 'number',
        builtInLabel: true,
        width: 3,
        validation: 'required|integer|min:0|max:9999',
        errors: {
            required: 'This field is required',
            integer: 'Sort should be a number',
            max: 'Sort should not be greater than 9999',
            min: 'Sort should be greater or equal to 0'
        }
    },
    textColour: {
        label: 'Text colour',
        type: 'color',
        value: defaultLoyaltyBalanceVisualisation.textColour,
        validation: 'required',
        width: 6,
        errors: {
            required: 'This field is required'
        }
    },
    backgroundOverlayColour: {
        label: 'Background color',
        type: 'color',
        width: 6,
        value: defaultLoyaltyBalanceVisualisation.backgroundOverlayColour,
        opacity: true
    },
    backgroundImageUrl: {
        label: 'Background image',
        type: 'image',
        aspect: 3 / 2
    },
    textAlignment: {
        label: 'Text alignment',
        type: 'select',
        width: 6,
        options: [
            { label: 'Left', value: 'LEFT' },
            { label: 'Center', value: 'CENTER' },
            { label: 'Right', value: 'RIGHT' }
        ],
        value: defaultLoyaltyBalanceVisualisation.textAlignment
    },
    verticalAlignment: {
        label: 'Vertical alignment',
        type: 'select',
        width: 6,
        options: [
            { label: 'Top', value: 'TOP' },
            { label: 'Middle', value: 'MIDDLE' },
            { label: 'Bottom', value: 'BOTTOM' }
        ],
        value: defaultLoyaltyBalanceVisualisation.verticalAlignment
    },
    description: {
        label: 'Description',
        type: 'text'
    },
    terms: {
        label: 'Terms and conditions',
        type: 'text'
    },
    subtitleText: {
        label: 'Subtitle',
        type: 'text',
        value: defaultLoyaltyBalanceVisualisation.subtitleText
    },
    subtitleTwoText: {
        label: 'Subtitle two',
        type: 'text'
    }
};

export function transformModuleToLoyaltyBalanceFormFields(module: ContentModule): any {
    if (isLoyaltyBalanceModule(module)) {
        return {
            ...module,
            textColour: module.properties?.visualisation?.textColour,
            backgroundImageUrl: module.properties?.visualisation?.backgroundImageUrl,
            backgroundOverlayColour: module.properties?.visualisation?.backgroundOverlayColour,
            subtitleText: module.properties?.visualisation?.subtitleText,
            subtitleTwoText: module.properties?.visualisation?.subtitleTwoText,
            textAlignment: module.properties?.visualisation?.textAlignment,
            verticalAlignment: module.properties?.visualisation?.verticalAlignment,
            description: module.properties?.visualisation?.description,
            terms: module.properties?.visualisation?.terms
        };
    }
    return module;
}

const getVisualisation = (formData: { [key: string]: any }) => {
    const opacity = getPerkOpacity(formData?.backgroundOverlayColour);
    return {
        textColour: formData?.textColour,
        backgroundImageUrl: formData?.backgroundImageUrl,
        backgroundOverlayColour: formData?.backgroundOverlayColour,
        backgroundOverlayOpacity: opacity,
        subtitleText: formData?.subtitleText,
        subtitleTwoText: formData?.subtitleTwoText,
        textAlignment: formData?.textAlignment,
        verticalAlignment: formData?.verticalAlignment,
        description: formData?.description,
        terms: formData?.terms
    };
};

export function transformLoyaltyBalanceFormFieldToModuleFields(
    fieldName: string,
    value: any,
    formData: { [key: string]: any }
): { name: string; value: any }[] {
    switch (fieldName) {
        case 'textColour':
            return [
                {
                    name: 'properties',
                    value: {
                        visualisation: {
                            ...getVisualisation(formData),
                            textColour: value
                        }
                    }
                }
            ];
        case 'backgroundImageUrl':
            return [
                {
                    name: 'properties',
                    value: {
                        visualisation: {
                            ...getVisualisation(formData),
                            backgroundImageUrl: value
                        }
                    }
                }
            ];
        case 'backgroundOverlayColour': {
            const opacity = getPerkOpacity(value);
            return [
                {
                    name: 'properties',
                    value: {
                        visualisation: {
                            ...getVisualisation(formData),
                            backgroundOverlayColour: value,
                            backgroundOverlayOpacity: opacity
                        }
                    }
                }
            ];
        }
        case 'subtitleText':
            return [
                {
                    name: 'properties',
                    value: {
                        visualisation: {
                            ...getVisualisation(formData),
                            subtitleText: value
                        }
                    }
                }
            ];
        case 'subtitleTwoText':
            return [
                {
                    name: 'properties',
                    value: {
                        visualisation: {
                            ...getVisualisation(formData),
                            subtitleTwoText: value
                        }
                    }
                }
            ];
        case 'textAlignment':
            return [
                {
                    name: 'properties',
                    value: {
                        visualisation: {
                            ...getVisualisation(formData),
                            textAlignment: value
                        }
                    }
                }
            ];
        case 'verticalAlignment':
            return [
                {
                    name: 'properties',
                    value: {
                        visualisation: {
                            ...getVisualisation(formData),
                            verticalAlignment: value
                        }
                    }
                }
            ];
        case 'description':
            return [
                {
                    name: 'properties',
                    value: {
                        visualisation: {
                            ...getVisualisation(formData),
                            description: value
                        }
                    }
                }
            ];
        case 'terms':
            return [
                {
                    name: 'properties',
                    value: {
                        visualisation: {
                            ...getVisualisation(formData),
                            terms: value
                        }
                    }
                }
            ];
        default:
            return [{ value, name: fieldName }];
    }
}
